import CloseIcon from "@mui/icons-material/Close";
import ScrollDownIcon from "@mui/icons-material/KeyboardArrowDown";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import DOMPurify from "dompurify";

import aiLogo from "../../../asset/images/aiLogo.png";
import pauseIcon from "../../../asset/images/pauseIcon.png";
import profileLogo from "../../../asset/images/profileLogo.png";
import selectIcon from "../../../asset/images/selectIcon.png";
import { getReferentielDataFromServer } from "../../../common/components/general/AxiosReferencielInterceptor";
import { useTSData } from "../../../common/components/general/TSDataContext";
import { PORTAIL_GU } from "../../../common/constants/generalConstants";
import { MESSAGING_PAGE_ROUTE } from "../../../common/constants/routerConstants";
import config from "../../../config";
import MessageAIComponent from "./MessageAIComponent";
import { PGUAssistantBotUI } from "./PGUAssistantBotUI";

const AssistantBot = ({
  sessionActive,
  setSessionActive,
  isMobile,
  isExpanded = false,
  mode = "init",
  setMode,
  addToHistory = () => {},
  handleClose,
  source,
  isRecording,
  speechSupported,
  startRecording,
  stopRecording,
  inputMessage, 
  setInputMessage,
  token
}) => {
  const { messageActuel, setMessageActuel } = useTSData();
  const [messages, setmessages] = useState([]);
  const [messageId, setMessageId] = useState(null);
  const [aiIsGenerating, setAiIsGenerating] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const chatRef = useRef(null);
  const [lineTyped, setLineTyped] = useState(0);
  const abortControllerRef = useRef(null);

  const fetchConverstion = async () => {
    const response = await getReferentielDataFromServer(
      true,
      config.baseURL + "api" + `/messages/${sessionActive}`,
      {},
      "get"
    );
    if (response.error) {
      let url = new URL(window.location.href);
      url.searchParams.delete("sessionId");
      window.history.pushState({}, "", url);
      setSessionActive(null);
      return;
    }
    setmessages(
      response.map((m) => ({
        id: m.id,
        sender: m.role === "human" ? "user" : "ai",
        text: m.content,
      }))
    );
  };

  useEffect(() => {
    if (mode === "init") {
      if (!!messageActuel) {
        handleSendMessage(messageActuel);
        setMessageActuel(null);
      } else if (!!sessionActive) fetchConverstion();
      else {
        setmessages([]);
      }
    }
  }, [messageActuel, sessionActive]);

  useEffect(() => {
    const fetchMessage = async () => {
      if (!!messageId) {
        let _messages = [...messages];
        let message = _messages.find(
          (m) => m.id === messageId && m.sender === "ai"
        );
        let userMessage = _messages.find(
          (m) => m.id === messageId && m.sender === "user"
        );

        if (message) {
          let _sessionId = sessionActive;
          if (!_sessionId) {
            _sessionId = uuidv4();
            setSessionActive(_sessionId);
            addToHistory({
              id: _sessionId,
              title:
                userMessage.text.slice(0, 50) +
                `${userMessage.text.length > 50 ? "..." : ""}`,
              date: new Date(),
            });
            let url = new URL(window.location.href);
            url.searchParams.set("sessionId", _sessionId);
            window.history.pushState({}, "", url);
          }

          abortControllerRef.current = new AbortController();
          const signal = abortControllerRef.current.signal;

          try {
            const response = await fetch(config.aiChatBotUrl + "/ask", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                session_id: _sessionId,
                query: userMessage.text,
              }),
              signal: signal,
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let partialData = "";

            setAiIsGenerating(true);

            setmessages((prevMessages) =>
              prevMessages.map((m) =>
                m.id === messageId && m.sender === "ai" ? { ...m, text: "" } : m
              )
            );

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              partialData += decoder.decode(value, { stream: true });

              const lines = partialData.split("[]");
              for (let i = 0; i < lines.length - 1; i++) {
                const line = lines[i];

                let token;
                if (line.startsWith("data: ")) {
                  token = line.replace("data: ", "");
                } else {
                  token = line || "\n";
                }
                setmessages((prevMessages) =>
                  prevMessages.map((m) =>
                    m.id === messageId && m.sender === "ai"
                      ? { ...m, text: m.text + token }
                      : m
                  )
                );
              }

              partialData = lines[lines.length - 1];

              if (chatRef.current) {
                chatRef.current.scrollTop = chatRef.current.scrollHeight;
              }
            }

            setmessages((prevMessages) =>
              prevMessages.map((m) =>
                m.id === messageId && m.sender === "ai"
                  ? { ...m, loading: false }
                  : m
              )
            );
            setAiIsGenerating(false);
          } catch (error) {
            if (error.name === "AbortError") {
              console.log("Fetch aborted");
            } else {
              console.error("Error in streaming:", error);
              message.error = true;
            }
            setAiIsGenerating(false);
          }
        }
      }
    };

    fetchMessage();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [messageId]);

  const handleScroll = () => {
    const scrollHeight = chatRef.current.scrollHeight;
    const clientHeight = chatRef.current.clientHeight;
    if (chatRef.current.scrollTop < scrollHeight - clientHeight) {
      setShowScrollDown(true);
    }
  };

  const handleDestroy = () => {
    handleClose();
    setSessionActive(null);
    setmessages([]);
  };

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (chatRef.current && aiIsGenerating) {
        chatRef.current.scrollTo({
          top: chatRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    });

    if (chatRef.current) {
      observer.observe(chatRef.current, {
        subtree: true,
        attributeOldValue: true,
        characterData: true,
        attributes: true,
        childList: true,
      });
    }

    return () => observer.disconnect();
  }, [aiIsGenerating]);

  useEffect(() => {
    const chatElement = chatRef.current;
    if (chatElement) {
      chatElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatElement) {
        chatElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const blockActuelMessage = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setAiIsGenerating(false);
    }
  };

  const handleSendMessage = (message) => {
    if (message && !aiIsGenerating) {
      setMode("messaging");
      let messageId = message.id || uuidv4();
      setAiIsGenerating(true);
      setmessages([
        ...messages,
        { id: messageId, text: message, sender: "user" },
        { id: messageId, text: "", sender: "ai", loading: true },
      ]);
      setMessageId(messageId);
    }
  };

  const getLineCount = (message) => {
    return message?.split("\n")?.length;
  };

  const handleSendInputMessage = () => {
    if (aiIsGenerating) {
      blockActuelMessage();
    } else if (inputMessage.trim() !== "" && !aiIsGenerating) {
      const lineCount = getLineCount(inputMessage);
      if (lineCount + lineTyped > 10) {
        setShowScrollDown(true);
      } else {
        setLineTyped(lineCount + lineTyped + 1);
      }
      handleSendMessage(inputMessage);
      setInputMessage("");
    }
  };

  let content;
  switch (source) {
    case PORTAIL_GU:
      content = (
        <PGUAssistantBotUI
          isMobile={isMobile}
          isExpanded={isExpanded}
          chatRef={chatRef}
          showScrollDown={showScrollDown}
          inputMessage={inputMessage}
          aiIsGenerating={aiIsGenerating}
          handleSendInputMessage={handleSendInputMessage}
          setInputMessage={setInputMessage}
          messages={messages}
          handleClose={handleClose}
          handleDestroy={handleDestroy}
          isRecording={isRecording}
          speechSupported={speechSupported}
          startRecording={startRecording}
          stopRecording={stopRecording}
          token={token}
        />
      );
      break;
    default:
      content = (
        <DefaultAssistantBotUI
          isMobile={isMobile}
          chatRef={chatRef}
          showScrollDown={showScrollDown}
          inputMessage={inputMessage}
          aiIsGenerating={aiIsGenerating}
          handleSendInputMessage={handleSendInputMessage}
          setInputMessage={setInputMessage}
          messages={messages}
          handleClose={handleClose}
          isRecording={isRecording}
          speechSupported={speechSupported}
          startRecording={startRecording}
          stopRecording={stopRecording}
        />
      );
      break;
  }
  return content;
};

const DefaultAssistantBotUI = (props) => {
  const {
    isMobile,
    chatRef,
    showScrollDown,
    inputMessage,
    aiIsGenerating,
    handleSendInputMessage,
    setInputMessage,
    messages,
    handleClose,
    isRecording,
    speechSupported,
    startRecording,
    stopRecording,
  } = props;

  return (
    <Box
      sx={{
        height: { xs: "76vh", sm: "460px", md: "460px", lg: "460px" },
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          position: "fixed",
          width: "95%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          borderRadius: "8px 8px 0 0",
          backgroundColor: "#E82930",
        }}
      >
        <Box
          sx={{
            marginLeft: "2px",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <img
            src={profileLogo}
            alt="Custom Icon"
            style={{
              margin: "5px",
              padding: 5,
              backgroundColor: "white",
              width: 35,
              height: 35,
              borderRadius: "50%",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{ color: "white", fontWeight: "bold" }}
          >
            {config.appName}
          </Typography>
          <Tooltip title={`Accéder à l'application ${config.appName}`}>
            <OpenInNewIcon
              sx={{ color: "white", cursor: "pointer" }}
              onClick={(e) => {
                window.open(config.frontUrl + MESSAGING_PAGE_ROUTE, "_blank");
              }}
            />
          </Tooltip>
        </Box>

        <IconButton
          onClick={handleClose}
          sx={{
            color: "white",
            marginRight: "10px",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Paper
        elevation={0}
        ref={chatRef}
        sx={{
          flex: 1,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          backgroundColor: "transparent",
          marginTop: "35px",
          padding: 2,
        }}
      >
        {messages.map((message, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent:
                message.sender === "user" ? "flex-end" : "flex-start",
              mb: 1,
            }}
          >
            {message.sender !== "user" && (
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: "40px",
                    height: "40px",
                    marginLeft: "10px",
                    marginTop: "10px",
                    border: "1px solid red",
                    borderRadius: "50%",
                  }}
                >
                  <img
                    src={aiLogo}
                    alt="AI Logo"
                    width="90%"
                    height="90%"
                    style={{
                      paddingLeft: "10px",
                      paddingTop: "5px",
                      paddingBottom: "5px",
                    }}
                  />
                </Box>
              </Box>
            )}

            <Box
              sx={{
                maxWidth: "70%",
                padding: "10px",
                margin: "10px",
                borderRadius: "13px",
                backgroundColor:
                  message.sender === "user" ? "#F6F6F6" : "#F5E2E3",
                color: message.sender === "user" ? "#0B0D0A" : "#000",
              }}
            >
              {message.sender === "user" ? (
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "16.53px",
                    fontWeight: 500,
                    lineHeight: "24.8px",
                    textAlign: "left",
                    textUnderlinePosition: "from-font",
                    textDecorationSkipInk: "none",
                    color: "#0B0D0A",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(message.text.replace(/\n/g, "<br />")),
                  }}
                />
              ) : (
                <MessageAIComponent message={message} />
              )}
            </Box>
          </Box>
        ))}
      </Paper>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignContent: "center",
          padding: 2,
        }}
      >
        {showScrollDown && (
          <Box
            sx={{
              position: "absolute",
              bottom: 75,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
            }}
          >
            <IconButton
              onClick={() => {
                if (chatRef.current) {
                  chatRef.current.scrollTop = chatRef.current.scrollHeight;
                }
              }}
            >
              <ScrollDownIcon sx={{ fontSize: 30, color: "#7E7E7E" }} />
            </IconButton>
          </Box>
        )}
        <TextField
          fullWidth
          name="message"
          multiline
          variant="outlined"
          placeholder={`Message ${config.appName}...`}
          value={inputMessage}
          onChange={(e) => {
            setInputMessage(e.target.value);
          }}
          sx={{
            "& .MuiInputLabel-root": {
              color: "#808080",
            },
            "& .MuiInputBase-root": {
              color: "#808080",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#808080",
              },
              "&:hover fieldset": {
                borderColor: "#808080",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#808080",
              },
            },
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.shiftKey) {
              setInputMessage((prevInput) => prevInput + "\n");
              e.preventDefault();
            } else if (e.key === "Enter") {
              handleSendInputMessage();
              e.preventDefault();
            }
          }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            sx: {
              backgroundColor: "#FFFFFF",
              borderColor: "inherit",
              borderRadius: "10px",
              maxHeight: isMobile ? "100px" : "200px",
              overflowY: "auto",
            },
            endAdornment: (
              <InputAdornment position="end">
                {speechSupported && (
                  <IconButton
                    onClick={isRecording ? stopRecording : startRecording}
                    sx={{
                      color: isRecording ? "#f44336" : "#666",
                      backgroundColor: isRecording
                        ? "rgba(244, 67, 54, 0.1)"
                        : "transparent",
                      marginRight: "8px",
                      position: "relative",
                      animation: isRecording ? "pulse 1.5s infinite" : "none",
                      "&::before": isRecording
                        ? {
                            content: '""',
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: "40px",
                            height: "40px",
                            backgroundColor: "rgba(244, 67, 54, 0.3)",
                            borderRadius: "50%",
                            transform: "translate(-50%, -50%)",
                            animation: "ripple 1.5s infinite",
                            zIndex: -1,
                          }
                        : {},
                    }}
                  >
                    {isRecording ? (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M6 6h12v12H6z" />
                      </svg>
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                      </svg>
                    )}
                  </IconButton>
                )}
                <IconButton onClick={handleSendInputMessage} edge="end">
                  <img
                    src={aiIsGenerating ? pauseIcon : selectIcon}
                    alt="Send Icon"
                    style={{
                      width: "28px",
                      height: "28px",
                      marginRight: "1px",
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default AssistantBot;
